import { useCallback, useEffect, useState } from 'react';
import { fetchGames } from '../lib/api';
import type { DisplayGame } from '@swipeopinion/shared';
import { useMe } from './useProfile';

const PAGE_SIZE = 20;

export interface UseGamesResult {
  games: DisplayGame[];
  total: number;
  loading: boolean;       // first page
  loadingMore: boolean;   // subsequent pages
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

/**
 * Games-wall data hook. Load-more / accumulate, mirroring useOffers — the API
 * supports page/perPage but only ever returned page 1 before, stranding the
 * ~688 LIVE games past the first 20. loadMore appends the next page.
 *
 * pub_member_id is NEVER sent from the client — the API injects the member from
 * the session; the wall is served from the synced catalog.
 */
export function useGames(): UseGamesResult {
  const { isSuccess: meReady } = useMe();
  const [games, setGames] = useState<DisplayGame[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!meReady) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchGames({ page: 1, perPage: PAGE_SIZE })
      .then((r) => {
        if (cancelled) return;
        setGames(r.games);
        setTotal(r.total);
        setPage(r.page);
        setHasMore(r.page * r.perPage < r.total);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meReady]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const r = await fetchGames({ page: next, perPage: PAGE_SIZE });
      setGames((prev) => [...prev, ...r.games]); // ACCUMULATE
      setTotal(r.total);
      setPage(r.page);
      setHasMore(r.page * r.perPage < r.total);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page]);

  return { games, total, loading, loadingMore, error, hasMore, loadMore };
}
